import { FiLayout } from 'react-icons/fi'
import { RxStack } from 'react-icons/rx'
import { TbFilter } from 'react-icons/tb'
import { Chip } from '~/@/components/ui/chip'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '~/@/components/ui/select'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DetailedTableTab } from '~/components/tables/types'

// Special value for "current view with unsaved filters"
export const EXPORT_SOURCE_CURRENT_VIEW = '__current_view__'
// Special value for "all records" (no tab/filters)
export const EXPORT_SOURCE_ALL_RECORDS = '__all_records__'

export type ExportSourceValue =
  | typeof EXPORT_SOURCE_CURRENT_VIEW
  | typeof EXPORT_SOURCE_ALL_RECORDS
  | string

interface ExportSourceSelectorProps {
  tableName: string
  tableTabs: DetailedTableTab[]
  selectedValue: ExportSourceValue
  onValueChange: (value: ExportSourceValue) => void
  /** Whether there are unsaved filter or sort changes in the current view */
  hasUnsavedChanges: boolean
  /** Number of filters in the current view (for display) */
  currentFilterCount?: number
}

/**
 * Unified dropdown for selecting the export source.
 * Combines current view (with unsaved indicator), all records, and saved views
 * into a single dropdown for better UX.
 */
export function ExportSourceSelector({
  tableName,
  tableTabs,
  selectedValue,
  onValueChange,
  hasUnsavedChanges,
  currentFilterCount = 0,
}: ExportSourceSelectorProps) {
  const { t } = useTypedTranslation()

  const getDisplayLabel = (value: ExportSourceValue) => {
    if (value === EXPORT_SOURCE_CURRENT_VIEW) {
      return (
        <span className="flex items-center gap-2">
          <TbFilter size={16} />
          <span>{t('table.export.current_view')}</span>
          {hasUnsavedChanges && (
            <Chip variant="warning">{t('table.export.unsaved_filters_badge')}</Chip>
          )}
        </span>
      )
    }
    if (value === EXPORT_SOURCE_ALL_RECORDS) {
      return (
        <span className="flex items-center gap-2">
          <FiLayout size={16} />
          <span>{t('table.export.all_records')}</span>
        </span>
      )
    }
    // It's a tab id
    const tab = tableTabs.find((tab) => tab.id === value)
    if (tab) {
      return (
        <span className="flex items-center gap-2">
          <RxStack size={16} />
          <span>
            {t('table.export.view_prefix')} {tab.name}
          </span>
        </span>
      )
    }
    return null
  }

  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-9">{t('table.export.export_from')}</label>
      <Select onValueChange={onValueChange} value={selectedValue}>
        <SelectTrigger alwaysShowChevron className="w-full" variant="outline">
          <SelectValue>{getDisplayLabel(selectedValue)}</SelectValue>
        </SelectTrigger>
        <SelectContent className="z-[1400]">
          {/* Current view option - shown first if there are unsaved filters */}
          {hasUnsavedChanges && (
            <>
              <SelectItem value={EXPORT_SOURCE_CURRENT_VIEW}>
                <div className="flex w-full items-center gap-2">
                  <TbFilter size={16} />
                  <div className="flex flex-1 flex-col">
                    <span>{t('table.export.current_view')}</span>
                    {currentFilterCount > 0 && (
                      <span className="text-xs text-gray-8">
                        {currentFilterCount === 1
                          ? t('table.export.n_filters_applied', { count: currentFilterCount })
                          : t('table.export.n_filters_applied_plural', {
                              count: currentFilterCount,
                            })}
                      </span>
                    )}
                  </div>
                  <Chip variant="warning">{t('table.export.unsaved_filters_badge')}</Chip>
                </div>
              </SelectItem>
              <SelectSeparator />
            </>
          )}

          {/* Saved views */}
          {tableTabs.map((tab) => (
            <SelectItem key={tab.id} value={tab.id}>
              <span className="flex items-center gap-2">
                <RxStack size={16} />
                <span>
                  {t('table.export.view_prefix')} {tab.name}
                </span>
              </span>
            </SelectItem>
          ))}

          {tableTabs.length > 0 && <SelectSeparator />}

          {/* All records option */}
          <SelectItem value={EXPORT_SOURCE_ALL_RECORDS}>
            <span className="flex items-center gap-2">
              <FiLayout size={16} />
              <span>{t('table.export.export_all_records_in')}</span>
              <span className="underline">{tableName}</span>
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  )
}
